import { useMemo, useCallback } from "react"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { blocksApiResponseSchema } from "../utils/schemas/blocks-schema"

/**
 * Hook to fetch blocks for a specific lesson
 * @param {string} lessonId - The ID of the lesson to fetch blocks for
 * @returns {Object} - { blocks, loading, error, refetch }
 */
export function useBlocks(lessonId) {
  // Memoize the endpoint to prevent unnecessary re-renders
  // IMPORTANT: Chaque lessonId doit avoir une URL unique pour éviter les problèmes de cache
  const endpoint = useMemo(() => {
    if (!lessonId) return null
    // Utiliser une URL unique pour chaque lessonId pour éviter les problèmes de cache
    return `${endpoints.moduleBlocks}?lessonId=${lessonId}`
  }, [lessonId])
  
  const shouldFetch = Boolean(lessonId)
  
  // Ne pas faire de requête si lessonId est null/undefined
  const {
    data: blocksData,
    loading,
    error,
    execute: executeFetch
  } = useGet(
    shouldFetch && endpoint ? endpoint : `${endpoints.moduleBlocks}?lessonId=disabled`,
    {
      schema: blocksApiResponseSchema,
      immediate: shouldFetch // Ne faire la requête que si shouldFetch est true
    }
  )

  const refetch = useCallback(() => executeFetch(undefined, { force: true }), [executeFetch])

  // Memoize the return object to prevent unnecessary re-renders
  // Filtrer les blocs par lessonId pour s'assurer qu'on n'affiche que les blocs de la bonne leçon
  const filteredBlocks = useMemo(() => {
    if (!shouldFetch || !blocksData?.blocks) return []
    // Filtrer par lessonId pour s'assurer qu'on n'affiche que les blocs de la bonne leçon
    // Le backend devrait déjà filtrer, mais on double-vérifie côté client
    return blocksData.blocks.filter(block => {
      // Vérifier si le bloc a un lessonId qui correspond
      const blockLessonId = block.lessonId || block.lesson?.id
      return blockLessonId === lessonId
    })
  }, [shouldFetch, blocksData, lessonId])

  return useMemo(() => ({
    blocks: filteredBlocks,
    loading: shouldFetch ? loading : false,
    error: shouldFetch ? error : null,
    refetch: shouldFetch ? refetch : () => {},
    total: shouldFetch ? (blocksData?.total || filteredBlocks.length) : 0,
    page: shouldFetch ? (blocksData?.page || 1) : 1,
    totalPages: shouldFetch ? (blocksData?.totalPages || 1) : 1
  }), [shouldFetch, filteredBlocks, loading, error, refetch, blocksData])
}

/**
 * Hook to fetch all blocks (without module filter)
 * @returns {Object} - { blocks, loading, error, refetch }
 */
export function useAllBlocks() {
  const {
    data: blocksData,
    loading,
    error,
    execute: refetch
  } = useGet(endpoints.moduleBlocks, {
    schema: blocksApiResponseSchema
  })

  return {
    blocks: blocksData?.blocks || [],
    loading,
    error,
    refetch,
    total: blocksData?.total || 0,
    page: blocksData?.page || 1,
    totalPages: blocksData?.totalPages || 1
  }
}
